import { useMemo } from "react";
import { type ColumnDef } from "@tanstack/react-table";
import { DataColumnHeader } from "@/components/molecules/table";
import {
  SelfContainedDataTable,
  selectedIncludesFn,
} from "@/components/organisms/SelfContainedDataTable";
import type { ActiveMinister } from "../types";

interface UncredentialedMinistersTableProps {
  data: ActiveMinister[];
  isLoading?: boolean;
  onRowClick?: (row: ActiveMinister) => void;
}

export function UncredentialedMinistersTable({
  data,
  isLoading = false,
  onRowClick,
}: UncredentialedMinistersTableProps) {
  const columns = useMemo<ColumnDef<ActiveMinister>[]>(
    () => [
      {
        accessorKey: "full_name",
        header: ({ column }) => (
          <DataColumnHeader column={column} title="Nombre Completo" />
        ),
        filterFn: selectedIncludesFn,
        cell: ({ row }) => (
          <div className="font-medium">{row.original.full_name || "—"}</div>
        ),
      },
      {
        accessorKey: "dni",
        header: ({ column }) => <DataColumnHeader column={column} title="CI" />,
        filterFn: selectedIncludesFn,
        cell: ({ row }) => row.original.dni || "—",
      },
      {
        accessorKey: "adb_code",
        header: ({ column }) => (
          <DataColumnHeader column={column} title="Registro ADB" />
        ),
        filterFn: selectedIncludesFn,
        cell: ({ row }) => row.original.adb_code || "—",
      },
      {
        id: "degree_name",
        accessorFn: (row) => row.degree?.name ?? "",
        header: ({ column }) => (
          <DataColumnHeader column={column} title="Grado" />
        ),
        filterFn: selectedIncludesFn,
        cell: ({ row }) => row.original.degree?.name || "—",
      },
      {
        id: "district_name",
        accessorFn: (row) => row.district?.name ?? "",
        header: ({ column }) => (
          <DataColumnHeader column={column} title="Distrito" />
        ),
        filterFn: selectedIncludesFn,
        cell: ({ row }) => row.original.district?.name || "—",
      },
      {
        id: "region_name",
        accessorFn: (row) => row.region?.name ?? "",
        header: ({ column }) => (
          <DataColumnHeader column={column} title="Región" />
        ),
        filterFn: selectedIncludesFn,
        cell: ({ row }) => row.original.region?.name || "—",
      },
      {
        accessorKey: "position",
        header: ({ column }) => (
          <DataColumnHeader column={column} title="Cargo" />
        ),
        filterFn: selectedIncludesFn,
        cell: ({ row }) => row.original.position || "—",
      },
    ],
    []
  );

  return (
    <SelfContainedDataTable
      data={data}
      columns={columns}
      isLoading={isLoading}
      getSearchableText={(row) => {
        const adbCode = row.adb_code ?? "";
        return `${adbCode} ${adbCode.replace(/\D/g, "")} ${row.full_name ?? ""} ${row.dni ?? ""}`;
      }}
      searchPlaceholder="Buscar por nombre, CI o registro ADB..."
      initialSorting={[{ id: "full_name", desc: false }]}
      onRowClick={onRowClick}
      getRowClassName={onRowClick ? () => "cursor-pointer" : undefined}
      emptyMessage="No se encontraron ministros sin credencial"
      pageSizeOptions={[20, 50, 100]}
    />
  );
}
